import { useContext, useEffect, useState } from 'react'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { decode } from 'html-entities'
import styled from 'styled-components'

import { Box } from '@awaze/design-system.components.box'
import { Button } from '@awaze/design-system.components.button'
import {
  ListViewBullets as ListViewIcon,
  Map as MapIcon,
} from '@awaze/design-system.components.icon'
import { Pagination } from '@awaze/design-system.components.pagination'
import { ResultsCount } from '@awaze/design-system.components.results-count'
import {
  SegmentedControl,
  SegmentedControls,
} from '@awaze/design-system.components.segmented-control'
import { SortOptions } from '@awaze/design-system.components.sort-options'
import { Stack } from '@awaze/design-system.components.stack'
import { PropertyList } from '@awaze/design-system.compositions.property-list'
import {
  Image,
  Property,
} from '@awaze/design-system.compositions.property-list/dist/property-list.types'
import { SearchControls } from '@awaze/design-system.compositions.search-controls'
import {
  SearchFormConfig,
  SearchFormFields,
} from '@awaze/design-system.compositions.search-form'
import { useWindowWidth } from '@awaze/design-system.utils.react'
import { PlacesProvider } from '@awaze/mfe-common.api.places-api'
import { getBrandConfig } from '@awaze/mfe-common.configs.brand'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  PAGINATION_CLICK,
  SORT_ORDER_UPDATED,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awaze/mfe-common.contexts.tms'
import { useWishlist } from '@awaze/mfe-common.contexts.wishlist'
import { Regions } from '@awaze/mfe-common.types-and-mocks.one-search-client'
import type {
  AccommodationType,
  CommonSite,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'

import { ConditionalMapBlock } from '@/components/ConditionalMapBlock/ConditionalMapBlock'
import { DynamicMap } from '@/components/DynamicMap/DynamicMap'
import {
  getIsDesktop,
  getIsTablet,
  onDesktop,
} from '@/components/utils/dimension'
import { PropertyListComposite } from '@/compositions/property-list'
import {
  DEFAULT_IMAGE_URL,
  FUSION_DATE_FORMAT,
  IMAGE_DOMAIN_BASE_URL,
  IMAGE_WIDTH_AWAZE,
  IMAGE_WIDTH_NOVASOL,
  NUM_OF_USP_TO_SHOW,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
  PRICE_FROM_NIGHTS,
  RESULTS_PAGE_SIZE,
  SORT_OPTIONS_ENUM,
} from '@/constants'
import { PERFORMANCE_MARKS } from '@/constants/performance-marks'
import { useAppConfig } from '@/context/app-config-provider'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { useDisplayContext } from '@/lib/display/displayContext'
import { SearchResultsTranslations } from '@/types'
import { Assert } from '@/utils/assert'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { setDisplayModeWrapper } from '@/utils/displayModeOnClickHelper'
import { getPropertyInfo } from '@/utils/getPropertyInfo'
import { scrollTo } from '@/utils/helpers'
import { isNovasolPoweredBrand } from '@/utils/isNovasolPoweredBrand'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'
import {
  getSortOptionsFromConfig,
  shouldDisplaySortOptions,
} from '@/utils/sortOptions/sortOptions'

import {
  carouselInteractFactory,
  formatUrlsForImageArray,
  getDepositPriceFormattingCallback,
  MerchBanner,
  removeFloorplanImagesFromImageArray,
} from './helpers'
import { StyledSearchResults } from './searchResults.style'

const ControlsContainer = styled(Stack)`
  padding-bottom: 0;
  ${onDesktop} {
    padding-bottom: 16px;
  }

  > div {
    padding-right: 0;
  }
`

export interface GetMarkersArgs {
  ne?: string
  sw?: string
}

export const SearchResults: React.FC<{
  openFilters: () => void
  defaultConfig: SearchFormConfig
  initialValues: SearchFormFields
  translations: SearchResultsTranslations
  isFilterDrawerOpen: boolean
}> = ({
  openFilters,
  defaultConfig,
  initialValues,
  translations,
  isFilterDrawerOpen,
}) => {
  const dispatch = useSearchContextDispatch()
  const { setDisplayMode, displayMode } = useDisplayContext()
  const { country, formatCurrencyToObject, formatDate, formatMessage, locale } =
    useIntlContext()

  const {
    aggregations,
    hasResults,
    loading,
    properties = [],
    searchCriteria,
    total = 0,
    totalPages = 1,
    totalSelectedFilters,
  } = useSearchContextState()

  const searchData = {
    aggregations: aggregations ?? null,
    properties,
    rhsLevel: 'rhs1',
    total,
  }

  Assert.defined(searchCriteria, 'searchCriteria should not be undefined!')

  const { accommodationType, nights, page, salesMarket, sort, start } =
    searchCriteria

  const { brand, clientHeader, contentfulLocale, currency, themeBrand } =
    useApplicationConfig()
  const tms = useContext(TmsContext)
  const setDisplayModeWithTms = setDisplayModeWrapper(tms, setDisplayMode)
  const {
    deposits,
    pdpUrlSegment,
    mapOptions: { maxZoomLevel },
  } = getBrandConfig(brand)
  const width = useWindowWidth()
  const isDesktop = width ? getIsDesktop(width) : false
  const isTablet = width ? getIsTablet(width) : false
  const isDatedSearch = Boolean(start)
  const displayPagination = totalPages >= 1

  const optiContext = useOptimizelyDecisionsContext()

  const params = new URLSearchParams(Object.entries(searchCriteria))
  params.set('newflex', 'true')
  const queryParams = params.toString()

  const shouldShowMapButton = displayMode === 'LIST'
  const shouldShowListButton = displayMode === 'MAP'

  // We only want to render the map client side, otherwise the Google Map loader
  const [shouldRenderMap, setShouldRenderMap] = useState(false)

  // TODO: Make the property list more resilient and not reliant on property field
  const isSite =
    accommodationType === 'lodges' || accommodationType === 'holiday-parks'

  const GD424WasNowPricing = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.GD_424_WASNOW_PRICING,
  )

  const {
    googleMapsApiKey: googleApiKey,
    PLACES_API_URI: placesApiUri,
    PLACES_API_AKAMAI_KEY: placesApiAkamaiKey,
  } = useAppConfig()

  const displaySortOptions = shouldDisplaySortOptions({
    displayMode,
  })

  let sortOptionsFromConfig = getSortOptionsFromConfig({
    accommodationType,
    brand,
    startDate: start,
  })

  if (brand === 'hoseasons' && accommodationType !== 'cottages') {
    sortOptionsFromConfig = [
      SORT_OPTIONS_ENUM.RECOMMENDED,
      SORT_OPTIONS_ENUM.PRICE_ASCENDING,
      SORT_OPTIONS_ENUM.PRICE_DESCENDING,
      SORT_OPTIONS_ENUM.REVIEWS_DESCENDING,
      SORT_OPTIONS_ENUM.NO_SORT,
    ]
  }
  if (
    brand === 'novasol' ||
    brand === 'cottages.com' ||
    brand === 'fincallorca' ||
    brand === 'jamesVillas' ||
    accommodationType === 'cottages'
  ) {
    sortOptionsFromConfig = [
      SORT_OPTIONS_ENUM.RECOMMENDED,
      SORT_OPTIONS_ENUM.PRICE_ASCENDING,
      SORT_OPTIONS_ENUM.PRICE_DESCENDING,
      SORT_OPTIONS_ENUM.SLEEPS_ASCENDING,
      SORT_OPTIONS_ENUM.SLEEPS_DESCENDING,
      SORT_OPTIONS_ENUM.REVIEWS_DESCENDING,
      SORT_OPTIONS_ENUM.NO_SORT,
    ]
  }

  const sortOptions = sortOptionsFromConfig.map((option) => ({
    text: formatMessage({
      id: `search-controls.sort.option-names.${option}`,
    }),
    value: option,
  }))

  const hasRecommendedSortOptionInConfig = sortOptions.some(
    (option) => option.value === 'recommended',
  )

  if (sort === 'recommended' && !hasRecommendedSortOptionInConfig) {
    sortOptions.unshift({ text: 'Recommended', value: 'recommended' })
  }

  const sortOptionLabels = {
    selectOption: formatMessage({
      id: 'search-controls.sort.select-option',
      defaultMessage: 'Select sort option',
    }),
    selectedOptionPrefix: formatMessage({
      id: 'search-controls.sort.selected-option-prefix',
      defaultMessage: 'Sort',
    }),
  }

  const pdpUrlSegmentTranslationKey =
    pdpUrlSegment[accommodationType as AccommodationType] ||
    pdpUrlSegment['default']

  useEffect(() => {
    setShouldRenderMap(true)
  }, [])

  const propertyImageWidth = isNovasolPoweredBrand(brand)
    ? IMAGE_WIDTH_NOVASOL
    : IMAGE_WIDTH_AWAZE

  const urgencyNudgeMessagingEnabled = brand === 'cottages.com'
  const urgencyNudgeMessagingText = "Book now before it's gone!" // BLAKE TO CONFIRM TEXT

  interface CommonSiteWithExtraProps extends CommonSite {
    urgencyNudgeMessaging?: boolean
    accommodationTypeId: string
  }

  const getPropertyListMappedProperty = (
    property: CommonSiteWithExtraProps,
  ): Property => {
    const imagesWithoutFloorplans = property.images
      ? removeFloorplanImagesFromImageArray(property.images)
      : []

    let finalImageArray: Image[]

    if (imagesWithoutFloorplans.length === 0) {
      finalImageArray = [
        {
          imageURL: DEFAULT_IMAGE_URL,
          imgAlt: `${decode(property.displayName)} images coming soon`,
        },
      ]
    } else {
      finalImageArray = formatUrlsForImageArray(
        imagesWithoutFloorplans,
        propertyImageWidth,
        brand,
      )
    }

    const remappedProperty = {
      ...property,
      property: {
        ...property.property,
      },
      images: finalImageArray,
      features: [],
      priceFrom: property.priceFrom ?? null,
      displayName: property.displayName || '',
      rhs3: property.rhs3 ?? '',
      grade: property.grade ?? '',
      location: property.location ?? '',
      averageReviewScore: property.averageReviewScore ?? 0,
      hasCallout: property.isCustomerChoice ?? false,
      hasPetsFree: property.petsGoFree ?? false,
      startDate:
        isSite && start
          ? format(parse(start, FUSION_DATE_FORMAT, new Date()), 'yyyy-MM-dd')
          : property.startDate || '',
      price: property.price ?? null,
      wasPrice: property.wasPrice ?? null,
      USPs: property.USPs ?? [],
      ...(property.lengthOfStay && { lengthOfStay: property.lengthOfStay }),
      urgencyNudgeMessaging:
        urgencyNudgeMessagingEnabled && property.urgencyNudgeMessaging,
    }

    if (
      !isDatedSearch &&
      (brand === 'cottages.com' || brand === 'hoseasons') &&
      GD424WasNowPricing !== 'variant_a'
    ) {
      remappedProperty.wasPrice = null
      remappedProperty.startDate = ''
    }

    return remappedProperty
  }

  const formattedPropertiesResponse = properties.map((property) =>
    getPropertyListMappedProperty(property),
  )

  const propertyListTranslations = {
    reserveTodayForOnly: formatMessage({
      id: 'search-results.property-list.reserve-today',
      defaultMessage: 'Reserve today for only',
    }),
    priceUnavailableCTALabel: formatMessage({
      id: 'search-results.property-list.unavailable-price',
      defaultMessage: 'For price, add a start date',
    }),
    calloutBadge: formatMessage({
      id: 'search-results.property-list.callout-badge',
      defaultMessage: "Customers' Choice",
    }),
    reviewRating: formatMessage({
      id: 'search-results.property-list.rating',
      defaultMessage: 'Feefo rating',
    }),
    outOf: formatMessage({
      id: 'search-results.property-list.out-of',
      defaultMessage: 'out of',
    }),
    stars: formatMessage({
      id: 'search-results.property-list.stars',
      defaultMessage: 'stars',
    }),
    nights: formatMessage({
      id: 'search-results.property-list.nights',
      defaultMessage: 'nights',
    }),
    from: formatMessage({
      id: 'search-results.property-list.from',
      defaultMessage: 'From',
    }),
    linkTo: formatMessage({
      id: 'search-results.property-list.link-to',
      defaultMessage: 'Link to',
    }),
    propertyLinkLabel: formatMessage({
      id: 'search-results.property-list.view',
      defaultMessage: 'View',
    }),
    petsFree: formatMessage({
      id: 'search-results.property-list.petsFree',
      defaultMessage: 'Pets stay free',
    }),
    urgencyBadge: formatMessage({
      id: 'search-results.property-list.urgencyMessage',
      defaultMessage: urgencyNudgeMessagingText,
    }),
    dateFormatter: (date: string) => {
      const UTCDate = new Date(`${date}T00:00:00`)

      return `${formatDate(UTCDate, { day: 'numeric' })} ${formatDate(UTCDate, {
        month: 'short',
      })}`
    },
    depositBanner: {
      reserveTodayForOnly: formatMessage({
        id: 'search-results.property-list.reserve-today',
        defaultMessage: 'Reserve today for only',
      }),
      reserveForSelectedProperties: formatMessage({
        id: 'search-results.property-list.reserve-for-selected-properties',
        defaultMessage: 'on selected properties',
      }),
    },
    includedExtrasLabel: formatMessage({
      id: 'search-results.property-list.includedExtras',
      defaultMessage: 'Price includes',
    }),
    propertyLinkAriaLabel: (title: string) =>
      formatMessage(
        {
          id: 'search-results.property-list.link-aria-label',
          defaultMessage: 'view {title} (opens in a new window)',
        },
        { title },
      ),
    formatCurrency: formatCurrencyToObject,
    propertySpecTranslations: {
      guests: {
        singular: formatMessage({
          id: 'property-specs.guests.singular',
          defaultMessage: 'guest',
        }),
        plural: formatMessage({
          id: 'property-specs.guests.plural',
          defaultMessage: 'guests',
        }),
      },
      studio: formatMessage({
        id: 'site.studio',
        defaultMessage: 'Studio',
      }),
      externalBathroom: formatMessage({
        id: 'site.external-bathroom',
        defaultMessage: 'External',
      }),
      bedrooms: {
        singular: formatMessage({
          id: 'site.bedrooms.singular',
          defaultMessage: 'Bedroom',
        }),
        plural: formatMessage({
          id: 'site.bedrooms.plural',
          defaultMessage: 'Bedrooms',
        }),
      },
      bathrooms: {
        singular: formatMessage({
          id: 'site.bathrooms.singular',
          defaultMessage: 'Bathroom',
        }),
        plural: formatMessage({
          id: 'site.bathrooms.plural',
          defaultMessage: 'Bathrooms',
        }),
      },
      pets: {
        singular: formatMessage({
          id: 'site.pets.singular',
          defaultMessage: 'Pet',
        }),
        plural: formatMessage({
          id: 'site.pets.plural',
          defaultMessage: 'Pets',
        }),
        label: formatMessage({
          id: 'site.up-to',
          defaultMessage: 'Up to',
        }),
        allowed: formatMessage({
          id: 'site.pets.allowed',
          defaultMessage: 'Allowed',
        }),
      },
    },
  }

  const PropertyCardsV2Variant = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.GD_PROPERTY_CARD_V2,
  )

  const SALES_MERCH_PODS = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.SALES_MERCH_PODS,
  )

  const imagePerformanceVariables = {
    numberImagesFetchPriorityHigh: 1,
    numberImagesPriority: 1,
    numberImagesToEagerLoad: 1,
  }

  const { isPropertyInWishlist, toggleWishlistItem } = useWishlist()

  const wishlistIconEnabledExp = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.APB_4017_WISHLIST_ENTRY_POINTS,
  )

  const wishlistButtonVisible =
    wishlistIconEnabledExp === 'wishlist_entry_points_visible'

  const addGD424Class = GD424WasNowPricing === 'variant_a' && !isDatedSearch

  return (
    <Stack fullWidth align="center" className={addGD424Class ? 'gdExp424' : ''}>
      <ControlsContainer
        isHorizontal
        fullWidth
        justify="space-between"
        align="baseline"
      >
        <span
          style={{
            opacity: !loading && !hasResults && displayMode === 'LIST' ? 0 : 1,
            paddingTop: 8,
            paddingBottom: 12,
          }}
        >
          <ResultsCount
            count={
              !hasResults && !loading ? 0 : total || ('—' as unknown as number)
            }
            labels={{
              textSingle: formatMessage({
                id: 'search-layout.property-count-single',
                defaultMessage: 'property',
              }),
              textMultiple: formatMessage({
                id: 'search-layout.property-count-multiple',
                defaultMessage: 'properties',
              }),
            }}
          />
        </span>

        {(hasResults || displayMode === 'MAP') && (
          <>
            {isDesktop && (
              <SegmentedControls>
                {displaySortOptions && (
                  <SortOptions
                    disablePadding
                    sortOptions={sortOptions}
                    selectedOption={sort ?? ''}
                    onOptionChange={(sort: string) =>
                      dispatch({ type: SORT_ORDER_UPDATED, sort })
                    }
                    labels={sortOptionLabels}
                  />
                )}
                {shouldShowMapButton && (
                  <Button
                    text={formatMessage({
                      id: 'search-controls.map.title',
                      defaultMessage: 'Map',
                    })}
                    icon={<MapIcon />}
                    onClick={() => {
                      globalThis?.performance?.mark(
                        PERFORMANCE_MARKS.MAP_VIEW_BUTTON_CLICKED,
                      )
                      setDisplayModeWithTms('MAP')
                      trackClientSideOptimizelyEvent(
                        OPTIMIZELY_EVENTS.MAP_CLICK,
                        optiContext,
                      )
                    }}
                    colourScheme="brand-secondary"
                    size="small"
                  />
                )}
                {shouldShowListButton && (
                  <SegmentedControl
                    text={formatMessage({
                      id: 'search-controls.list.title',
                      defaultMessage: 'Back to list view',
                    })}
                    ariaLabel={formatMessage({
                      id: 'search-controls.list.aria',
                      defaultMessage: 'Back to list view',
                    })}
                    icon={<ListViewIcon />}
                    variant="medium"
                    onClick={() => {
                      setDisplayModeWithTms('LIST')
                      dispatch({ type: PAGINATION_CLICK, page: 1 })
                    }}
                  />
                )}
              </SegmentedControls>
            )}
            {isTablet && (
              <SearchControls
                filtersCount={totalSelectedFilters?.toString() || '0'}
                {...(displaySortOptions && {
                  sorting: {
                    sortOptions: sortOptions,
                    selectedOption: sort || '',
                    onOptionChange: (sort: string) =>
                      dispatch({ type: SORT_ORDER_UPDATED, sort }),
                    labels: sortOptionLabels,
                  },
                })}
                showFilters={openFilters}
                justify="end"
                displayMode={displayMode}
                setDisplayMode={setDisplayModeWithTms}
                translations={{
                  filterTitle: formatMessage({
                    id: 'search-controls.filter.title',
                    defaultMessage: 'Filters',
                  }),
                  filterAriaText: formatMessage({
                    id: 'search-controls.filter.aria',
                    defaultMessage: 'Open filters',
                  }),
                  listTitle: formatMessage({
                    id: 'search-controls.list.title',
                    defaultMessage: 'Back to list view',
                  }),
                  listAriaText: formatMessage({
                    id: 'search-controls.list.aria',
                    defaultMessage: 'Back to list view',
                  }),
                  mapTitle: formatMessage({
                    id: 'search-controls.map.title',
                    defaultMessage: 'Map',
                  }),
                  mapAriaText: formatMessage({
                    id: 'search-controls.map.aria',
                    defaultMessage: 'Map View',
                  }),
                }}
              />
            )}
          </>
        )}
      </ControlsContainer>

      {shouldRenderMap && displayMode === 'MAP' && (
        // IMPORTANT Provider scope - See ADR: adr/[2026-01-12]-grapql-providers.md
        <PlacesProvider
          brand={brand}
          language={contentfulLocale}
          placesApiUri={placesApiUri}
          headers={{
            'x-awaze-akamai': placesApiAkamaiKey,
            'x-awaze-client': clientHeader,
          }}
        >
          <ConditionalMapBlock
            defaultConfig={defaultConfig}
            initialValues={initialValues}
            isDesktop={isDesktop}
            locale={locale}
            marketCode={searchCriteria.salesMarket || '826'}
            openFilters={openFilters}
            translations={translations}
            enableRemoveScroll={!isFilterDrawerOpen}
          >
            <DynamicMap
              maxZoom={maxZoomLevel}
              apiKey={googleApiKey || ''}
              brand={brand}
              themeBrand={themeBrand}
              fullHeight={!isDesktop}
              wishlistButtonVisible={wishlistButtonVisible}
              getPropertyInfo={(propertyId) =>
                getPropertyInfo(
                  {
                    brand,
                    propertyId,
                    salesMarket,
                    searchCriteria,
                  },
                  clientHeader,
                  currency,
                  contentfulLocale,
                )
              }
              translations={{
                failedMapPlaceholderHeading: formatMessage({
                  id: 'location-section.map',
                  defaultMessage: 'Map',
                }),
                failedMapPlaceholderText: formatMessage({
                  id: 'location-section.map-failed',
                  defaultMessage: 'Failed to load map. Please try and reload.',
                }),
                searchFormMapAreaText: formatMessage({
                  id: 'search-results.map.searchFormMapAreaText',
                  defaultMessage: 'Map area',
                }),
              }}
              pdpUrlSegment={formatMessage({
                id: pdpUrlSegmentTranslationKey,
              })}
            />
          </ConditionalMapBlock>
        </PlacesProvider>
      )}
      {displayMode === 'LIST' &&
        (PropertyCardsV2Variant === 'variant_a' ? (
          <PropertyListComposite
            properties={properties}
            deposits={deposits}
            translations={{
              ...propertyListTranslations,
              grade: formatMessage({
                id: 'property-specs.our-grade',
                defaultMessage: 'Star rating',
              }),
              guestRating: formatMessage({
                id: 'property-specs.guest-rating',
                defaultMessage: 'Guest rating',
              }),
              studioApt: formatMessage({
                id: 'property-specs.studio',
                defaultMessage: 'Studio',
              }),
              external: formatMessage({
                id: 'property-specs.externalBathroom',
                defaultMessage: 'External',
              }),
              bedrooms: formatMessage({
                id: 'property-specs.bedrooms.plural',
                defaultMessage: 'Bedrooms',
              }),
              bathrooms: formatMessage({
                id: 'property-specs.bathrooms.plural',
                defaultMessage: 'Bathrooms',
              }),
              pets: formatMessage({
                id: 'property-specs.pets.plural',
                defaultMessage: 'Pets',
              }),
              guests: formatMessage({
                id: 'property-specs.guests.plural',
                defaultMessage: 'Guests',
              }),
              petsFree: formatMessage({
                id: 'property-specs.pets-free',
                defaultMessage: 'Pets free',
              }),
            }}
            config={{
              brand: brand,
              searchCriteria: searchCriteria,
              searchData: {
                ...searchData,
                rhsLevel: (searchData.rhsLevel ?? 'rhs1') as Regions,
              },
              locale: locale,
              dateToday: new Date(),
              searchParameters: queryParams,
              accommodationType:
                accommodationType === 'cottages' ? 'cottages' : 'accom',
              pdpUrlSegment: formatMessage({
                id: pdpUrlSegmentTranslationKey,
              }),
              startDate: start,
              siteSearch: isSite,
              datedSearch: isDatedSearch,
              country,
            }}
            onCarouselInteractFactory={(imageArray, displayName, code) =>
              carouselInteractFactory(
                imageArray,
                displayName,
                tms,
                code.toString(),
              )
            }
            getWishlistButtonProps={(propertyCode) => ({
              showWishlistButton: wishlistButtonVisible,
              isPropertyInWishlist: isPropertyInWishlist(
                propertyCode.toString(),
              ),
              toggleWishlistItem: () => {
                toggleWishlistItem(
                  propertyCode.toString(),
                  searchCriteria as Record<string, unknown>,
                )
                tms?.updateDataLayer(
                  'link',
                  isPropertyInWishlist(propertyCode.toString())
                    ? 'wishlist_click_remove'
                    : 'wishlist_click_add',
                )
              },
            })}
          />
        ) : (
          <StyledSearchResults>
            <PropertyList
              loading={loading ?? false}
              results={formattedPropertiesResponse}
              nights={
                isDatedSearch
                  ? (nights ?? PRICE_FROM_NIGHTS)
                  : PRICE_FROM_NIGHTS
              }
              config={{
                translations: propertyListTranslations,
                USPCount: NUM_OF_USP_TO_SHOW,
                baseImg: IMAGE_DOMAIN_BASE_URL,
                defaultImg: DEFAULT_IMAGE_URL,
                depositBanner: {
                  country,
                  depositPriceFormattingCallback:
                    getDepositPriceFormattingCallback(
                      brand,
                      formatCurrencyToObject,
                    ),
                  formatCurrencyToObject,
                },
                resultsPageSize: RESULTS_PAGE_SIZE,
                dateToday: new Date(),
                searchParameters: queryParams,
                accommodationType:
                  brand === 'novasol' ? 'novasol_cottages' : accommodationType,
                pdpUrlSegment: formatMessage({
                  id: pdpUrlSegmentTranslationKey,
                }),
                imageWidth: propertyImageWidth,
                numberImagesFetchPriorityHigh:
                  imagePerformanceVariables.numberImagesFetchPriorityHigh,
                numberImagesPriority:
                  imagePerformanceVariables.numberImagesPriority,
                numberImagesToEagerLoad:
                  imagePerformanceVariables.numberImagesToEagerLoad,
              }}
              onPropertyLinkClick={({ propertyCode }) => {
                new DataLayerFactory()
                  .getEvent(DataLayerEventType.PropertyClick)
                  .update(
                    tms,
                    {
                      propertyCode,
                      searchCriteria,
                      searchData,
                    },
                    dataLayerErrorHandler,
                  )
                trackClientSideOptimizelyEvent(
                  OPTIMIZELY_EVENTS.PDP_CLICK_THROUGH,
                  optiContext,
                )
              }}
              deposits={deposits ?? []}
              onCarouselInteractFactory={(
                imageArray: Array<Image>,
                displayName: string,
                code: string | number,
              ) =>
                carouselInteractFactory(
                  imageArray,
                  displayName,
                  tms,
                  code.toString(),
                )
              }
              getWishlistButtonProps={(propertyCode) => ({
                showWishlistButton: wishlistButtonVisible,
                isPropertyInWishlist: isPropertyInWishlist(
                  propertyCode.toString(),
                ),
                toggleWishlistItem: () => {
                  toggleWishlistItem(
                    propertyCode.toString(),
                    searchCriteria as Record<string, unknown>,
                  )
                  tms?.updateDataLayer(
                    'link',
                    isPropertyInWishlist(propertyCode.toString())
                      ? 'wishlist_click_remove'
                      : 'wishlist_click_add',
                  )
                },
              })}
            />
            {SALES_MERCH_PODS === 'variant_a' && (
              <MerchBanner
                brand={brand}
                variant={SALES_MERCH_PODS}
                locale={locale}
                optiContext={optiContext}
              />
            )}
          </StyledSearchResults>
        ))}

      {displayPagination && displayMode === 'LIST' && (
        <Box padding={[16, 16, 16]}>
          <Pagination
            ariaTranslations={{
              ariaLabel: formatMessage({
                id: 'search-results.pagination.aria-page',
                defaultMessage: 'Page',
              }),
              ariaLabelActive: formatMessage({
                id: 'search-results.pagination.aria-current-page',
                defaultMessage: 'Current page, Page',
              }),
              ariaNextPage: formatMessage({
                id: 'search-results.pagination.next',
                defaultMessage: 'next',
              }),
              ariaPreviousPage: formatMessage({
                id: 'search-results.pagination.previous',
                defaultMessage: 'previous',
              }),
            }}
            activePage={page}
            totalPages={totalPages}
            onPaginationClick={(page) => {
              scrollTo(document.body)
              setTimeout(() => {
                dispatch({ type: PAGINATION_CLICK, page })
              }, 100)
            }}
          />
        </Box>
      )}
    </Stack>
  )
}
